import { has } from "lodash";
import { from, Observable, of, Subject } from "rxjs";
import { share, switchMap, throttleTime } from "rxjs/operators";
import { store } from "..";
import { API } from "../../services/http";
import { Pageable } from "../../services/pageable";
import { BillingDTO } from "../billing/models/dto";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { processErrorFromPromise } from "../errors/utils";
import { successNotificationFromPromise } from "../notifications/utils";
import { VaultActions } from "./actions";
import { VaultsDTO } from "./models/dto";

export namespace VaultService {
  const api = new API("vault");

  const getVaultsSubject = new Subject<Parameters<typeof getVaults>>();

  const getVaults$ = getVaultsSubject.pipe(
    throttleTime(10000),
    switchMap(([pagination = {}]) =>
      api
        .get(`v1/vaults`)
        .query(Pageable.getQueryParam(pagination))
        .then<FetchableSuccess<VaultsDTO.Responses.GetAllVaults.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    ),
    share()
  );

  export function getVaults(
    pagination: Pageable.T<keyof VaultsDTO.Responses.GetSingleVault.T> = {}
  ): Observable<
    FetchableError | FetchableSuccess<VaultsDTO.Responses.GetAllVaults.T>
  > {
    getVaultsSubject.next([pagination]);
    return getVaults$;
  }

  export function getSingleVault(
    id: VaultsDTO.Responses.GetSingleVault.T["id"]
  ): Promise<VaultsDTO.Responses.GetSingleVault.T> {
    const { singleVaultMap } = store.getState().vaults;

    if (has(singleVaultMap, id) && singleVaultMap[id] !== "LOADING") {
      return Promise.resolve(
        //TOOD fix for concurrency
        singleVaultMap[id] as VaultsDTO.Responses.GetSingleVault.T
      );
    }

    store.dispatch(VaultActions.getSingleVaults(id));
    return api
      .get(`v1/vaults/${id}`)
      .then<VaultsDTO.Responses.GetSingleVault.T>((response) => {
        store.dispatch(
          VaultActions.setSingleVaults({
            id,
            result: response.body,
          })
        );
        return response.body;
      })
      .catch<VaultsDTO.Responses.GetSingleVault.T>(processErrorFromPromise);
  }

  export async function addVault(
    data: VaultsDTO.Requests.Create.T
  ): Promise<VaultsDTO.Responses.CreateVault.T> {
    return api
      .post(`v1/vaults`)
      .send(data)
      .then<VaultsDTO.Responses.CreateVault.T>((response) => response.body)
      .then((p) =>
        successNotificationFromPromise(`vault-has-been-created`)(p)
      )
      .then((p) => {
        store.dispatch(VaultActions.resetAllVaults());
        return p;
      })
      .catch<VaultsDTO.Responses.CreateVault.T>(processErrorFromPromise);
  }

  export function getVaultEntries(
    vaultId: VaultsDTO.Responses.GetSingleVault.T["id"],
    pagination: Pageable.T<keyof VaultsDTO.Responses.GetSingleEntry.T> = {}
  ): Observable<
    FetchableError | FetchableSuccess<VaultsDTO.Responses.GetAllEntries.T>
  > {
    return from(
      api
        .get(`v1/${vaultId}/vaults`)
        .query(Pageable.getQueryParam(pagination))
        .then<FetchableSuccess<VaultsDTO.Responses.GetAllEntries.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  // Billing

  export function getBilling(): Observable<
    FetchableError | FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T>
  > {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return of({
        status: "error",
        err: new Error("User not logged in"),
      });

    return from(
      api
        .get(`billing/${login.data.accessToken.credentialsId}/apis`)
        .then<FetchableSuccess<BillingDTO.Responses.GetBillingInfo.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }
}
